import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, RotateCcw, PackageSearch, Tag, ShoppingBag, ClipboardList, Sparkles, TrendingUp, Megaphone, Copy, Link2, Share2 } from "lucide-react";
import { useCatalog } from "@/context/CatalogContext";
import { useCart } from "@/context/CartContext";
import { useStock } from "@/context/StockContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { pending: "Received", paid: "Confirmed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };

export default function Account() {
  const navigate = useNavigate();
  const { user, loading: authLoading, signIn, signOut } = useCustomerAuth();
  const { getProduct } = useCatalog();
  const { addItem, setOpen } = useCart();
  const { isSoldOut } = useStock();

  const [legacyEmail, setLegacyEmail] = useState(() => localStorage.getItem("mx_email") || "");
  const email = user?.email || legacyEmail;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const [amb, setAmb] = useState(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("auth") === "failed") {
      toast.error("Google sign-in failed. Please try again.");
      window.history.replaceState({}, document.title, "/account");
    }
  }, []);

  useEffect(() => {
    try { const p = localStorage.getItem("mx_promo"); if (p) setPromo(JSON.parse(p)); } catch (e) { /* ignore */ }
  }, []);

  const loadOrders = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      if (user) {
        const { data } = await axios.get(`${API}/account/my-orders`, { withCredentials: true });
        setOrders(data);
      } else {
        const { data } = await axios.post(`${API}/account/orders`, { email });
        setOrders(data);
      }
    } catch (err) {
      toast.error("Could not load your orders");
    } finally { setLoading(false); }
  }, [email, user]);

  const loadAmbassador = useCallback(async () => {
    if (!user) { setAmb(null); return; }
    try {
      const { data } = await axios.get(`${API}/account/ambassador`, { withCredentials: true });
      setAmb(data.ambassador);
    } catch (e) { setAmb(null); }
  }, [user]);

  const [applying, setApplying] = useState(false);
  const applyAmbassador = async () => {
    if (!user || applying) return;
    setApplying(true);
    try {
      await axios.post(`${API}/ambassadors/apply`, { name: user.name || user.email.split("@")[0], email: user.email });
      toast.success("Application submitted — we'll review it shortly!");
      await loadAmbassador();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit your application";
      if (err?.response?.status === 409) { toast(msg); await loadAmbassador(); }
      else toast.error(msg);
    } finally { setApplying(false); }
  };

  useEffect(() => { if (email) loadOrders(); }, [email, loadOrders]);
  useEffect(() => { loadAmbassador(); }, [loadAmbassador]);

  const reorder = (order) => {
    let added = 0, skipped = 0;
    order.items.forEach((it) => {
      const p = getProduct(it.id);
      if (!p) return;
      if (isSoldOut(p)) { skipped++; return; }
      addItem(p, it.qty); added++;
    });
    if (added) { setOpen(true); toast.success(`Reordered ${added} item(s) from ${order.order_ref}${skipped ? ` · ${skipped} sold out and skipped` : ""}`); }
    else if (skipped) toast.error("Those items are currently sold out");
    else toast.error("These items are no longer available");
  };

  const restoreCart = async () => {
    try {
      const { data } = await axios.post(`${API}/carts/mine`, { email });
      if (!data.items || data.items.length === 0) { toast("No saved cart found", { description: "Add items and they'll be saved automatically." }); return; }
      let added = 0, skipped = 0;
      data.items.forEach((it) => {
        const p = getProduct(it.id);
        if (!p) return;
        if (isSoldOut(p)) { skipped++; return; }
        addItem(p, it.qty); added++;
      });
      if (added) { setOpen(true); toast.success(`Restored ${added} item(s)${skipped ? ` · ${skipped} sold out and skipped` : ""}`); }
      else if (skipped) toast.error("Your saved items are currently sold out");
      else toast.error("Saved items are no longer available");
    } catch (e) { toast.error("Could not restore your cart"); }
  };

  const logout = async () => {
    if (user) { await signOut(); }
    localStorage.removeItem("mx_email");
    setLegacyEmail("");
    toast.success("Signed out");
    setTimeout(() => { window.location.href = "/"; }, 400);
  };

  // ---- Loading auth state ----
  if (authLoading) {
    return (
      <main className="pt-32 pb-24 min-h-screen flex items-center justify-center" data-testid="account-page">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#52525B] animate-pulse">Loading your account…</p>
      </main>
    );
  }

  // ---- Signed out (no Google session, no legacy email) ----
  if (!email) {
    return (
      <main className="pt-32 pb-24 min-h-screen" data-testid="account-page">
        <div className="max-w-md mx-auto px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Your Account</p>
          <h1 className="font-serif text-4xl font-light mb-8">Sign in</h1>
          <GoogleButton onClick={signIn} />
          <div className="flex items-center gap-4 my-8">
            <span className="flex-1 h-px bg-zinc-800" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">or use your email</span>
            <span className="flex-1 h-px bg-zinc-800" />
          </div>
          <SignInForm onDone={(e) => setLegacyEmail(e)} />
          <button onClick={() => navigate("/")} className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-[#52525B] hover:text-[#A1A1AA]">Back to store</button>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-28 min-h-screen" data-testid="account-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-12">
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full object-cover ring-1 ring-[#D4AF37]/40" data-testid="account-avatar" />
            )}
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Your Account</p>
              <h1 className="font-serif text-4xl md:text-5xl font-light leading-none">{user?.name ? `Hi, ${user.name.split(" ")[0]}` : "Dashboard"}</h1>
              <p className="font-mono text-sm text-[#A1A1AA] mt-3" data-testid="account-email">Signed in as {email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="account-logout-btn"
            className="flex items-center gap-2 border border-zinc-700 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-red-400/60 hover:text-red-400 transition-colors">
            <LogOut size={14} /> Log out
          </button>
        </div>

        {/* Prompt legacy email users to upgrade to Google */}
        {!user && (
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="account-google-nudge">
            <p className="font-mono text-xs text-[#D4AF37]">Sign in with Google to secure your account and unlock your ambassador dashboard.</p>
            <button onClick={signIn} data-testid="account-google-upgrade-btn"
              className="border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors whitespace-nowrap">
              Continue with Google
            </button>
          </div>
        )}

        {/* Ambassador / store-credit tie-in */}
        {user && <AmbassadorCard amb={amb} onApply={applyAmbassador} applying={applying} />}

        {/* Perks row */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="account-promo">
            <div className="flex items-center gap-2 mb-3"><Tag size={15} color="#D4AF37" /><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Your code</span></div>
            {promo ? (
              <>
                <p className="font-serif text-3xl text-[#D4AF37]">{promo.code}</p>
                <p className="font-mono text-xs text-[#71717A] mt-1">{promo.percent}% off — applied automatically at checkout</p>
              </>
            ) : (
              <p className="font-mono text-sm text-[#52525B]">No active code on this device.</p>
            )}
          </div>
          <div className="border border-zinc-800 bg-[#0C0C0C] p-6 flex flex-col" data-testid="account-saved-cart">
            <div className="flex items-center gap-2 mb-3"><ShoppingBag size={15} color="#D4AF37" /><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Saved cart</span></div>
            <p className="font-mono text-sm text-[#71717A] mb-4 flex-1">Pick up where you left off — your cart is saved to your email.</p>
            <button onClick={restoreCart} data-testid="account-restore-cart-btn"
              className="self-start border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">
              Restore saved cart
            </button>
          </div>
        </div>

        {/* Order history */}
        <div className="flex items-center gap-2 mb-6">
          <ClipboardList size={16} color="#D4AF37" />
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-[#A1A1AA]">Order History</h2>
        </div>

        {loading && <p className="font-mono text-sm text-[#52525B] py-10" data-testid="account-loading">Loading your orders…</p>}

        {!loading && orders.length === 0 && (
          <div className="flex flex-col items-center text-center py-16 text-[#52525B] border border-zinc-900" data-testid="account-empty">
            <PackageSearch size={40} strokeWidth={1} className="mb-4" />
            <p className="font-mono text-sm">No orders yet.</p>
            <Link to="/" className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">Browse the catalog →</Link>
          </div>
        )}

        <div className="space-y-4">
          {orders.map((o, idx) => {
            const cancelled = o.status === "cancelled";
            return (
              <motion.div key={o.order_ref} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid={`account-order-${o.order_ref}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <p className="font-serif text-2xl">{o.order_ref}</p>
                    <p className="font-mono text-[11px] text-[#71717A] mt-1">Placed {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${cancelled ? "text-red-400 border-red-400/40" : "text-[#D4AF37] border-[#D4AF37]/40"}`} data-testid={`account-order-status-${o.order_ref}`}>
                    {LABELS[o.status] || o.status}
                  </span>
                </div>
                <div className="space-y-1.5 mb-5">
                  {o.items.map((i) => (
                    <div key={i.id} className="flex justify-between text-sm border-b border-zinc-900 py-1.5">
                      <span className="text-[#A1A1AA]">{i.name} ({i.size}) ×{i.qty}</span>
                      <span className="font-mono text-[#D4AF37]">${i.lineTotal}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">Total</span>
                    <span className="font-serif text-xl">${o.total}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => reorder(o)} data-testid={`account-reorder-${o.order_ref}`}
                    className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">
                    <RotateCcw size={13} /> Reorder
                  </button>
                  <Link to={`/track?ref=${encodeURIComponent(o.order_ref)}&email=${encodeURIComponent(email)}`} data-testid={`account-track-${o.order_ref}`}
                    className="flex items-center gap-2 border border-zinc-700 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                    <PackageSearch size={13} /> Track
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </main>
  );
}

function AmbassadorCard({ amb, onApply, applying }) {
  if (!amb) {
    return (
      <div className="border border-zinc-800 bg-[#0C0C0C] p-6 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="account-ambassador-join">
        <div className="flex items-center gap-3">
          <Sparkles size={18} color="#D4AF37" />
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Molecularix Elite</p>
            <p className="font-mono text-[11px] text-[#71717A] mt-1">Earn commission and store credit by referring researchers.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/ambassador" data-testid="account-ambassador-learn-link"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#D4AF37] transition-colors whitespace-nowrap">
            Learn more
          </Link>
          <button onClick={onApply} disabled={applying} data-testid="account-ambassador-apply-btn"
            className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50 whitespace-nowrap">
            <Sparkles size={13} /> {applying ? "Applying…" : "Apply in one tap"}
          </button>
        </div>
      </div>
    );
  }
  if (amb.status !== "approved") {
    return (
      <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6 mb-8" data-testid="account-ambassador-pending">
        <div className="flex items-center gap-2 mb-2"><Sparkles size={16} color="#D4AF37" /><span className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Elite application {amb.status}</span></div>
        <p className="font-mono text-[11px] text-[#71717A]">We&apos;re reviewing your ambassador application. You&apos;ll be notified by email once approved.</p>
      </div>
    );
  }
  return (
    <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 mb-8" data-testid="account-ambassador-active">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-2"><Sparkles size={16} color="#D4AF37" /><span className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Molecularix Elite · Ambassador</span></div>
        <Link to="/ambassador" data-testid="account-ambassador-dashboard-link"
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">Full dashboard →</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Your code" value={amb.code} />
        <Stat label="Unpaid" value={`$${(amb.unpaid ?? 0).toFixed ? amb.unpaid.toFixed(2) : amb.unpaid}`} icon />
        <Stat label="Earned" value={`$${(amb.earned ?? 0).toFixed ? amb.earned.toFixed(2) : amb.earned}`} />
        <Stat label="Referrals" value={amb.referrals ?? 0} />
      </div>
      <ReferralShare code={amb.code} discount={amb.buyer_discount} />
    </div>
  );
}

function ReferralShare({ code, discount }) {
  const pct = discount || 10;
  const link = `${window.location.origin}/?ref=${code}`;
  const caption =
    `Sourcing reference-grade peptides & research compounds? I use Molecularix Biolabs.\n\n` +
    `Use code ${code} for ${pct}% off your order — it auto-applies at checkout:\n${link}\n\n` +
    `For research use only. Not for human consumption.`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      toast.error("Couldn't copy — please copy it manually");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Molecularix Biolabs", text: caption, url: link }); }
      catch (e) { /* user dismissed */ }
    } else {
      copy(caption, "Caption");
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-[#D4AF37]/20" data-testid="account-ambassador-share">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone size={14} color="#D4AF37" />
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Share &amp; earn</span>
      </div>
      <p className="font-mono text-[11px] text-[#71717A] mb-3">Ready to post — copy the caption below and drop it anywhere. Your link auto-applies the discount.</p>
      <textarea readOnly value={caption} data-testid="account-ambassador-caption"
        rows={5}
        className="w-full bg-[#0C0C0C] border border-zinc-800 p-3 text-sm text-[#E8E8E3] leading-relaxed resize-none outline-none focus:border-[#D4AF37]/50 transition-colors" />
      <div className="flex flex-wrap gap-3 mt-3">
        <button onClick={() => copy(caption, "Caption")} data-testid="account-ambassador-copy-caption"
          className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors">
          <Copy size={13} /> Copy caption
        </button>
        <button onClick={() => copy(link, "Referral link")} data-testid="account-ambassador-copy-link"
          className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">
          <Link2 size={13} /> Copy link
        </button>
        <button onClick={share} data-testid="account-ambassador-share-btn"
          className="flex items-center gap-2 border border-zinc-700 text-[#A1A1AA] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          <Share2 size={13} /> Share
        </button>
        <Link to="/ambassador" data-testid="account-ambassador-assets-link"
          className="flex items-center gap-2 border border-zinc-700 text-[#A1A1AA] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
          <Sparkles size={13} /> Get graphics
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        {icon && <TrendingUp size={12} color="#D4AF37" />}
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#71717A]">{label}</span>
      </div>
      <p className="font-serif text-xl text-[#F5F5F0]">{value}</p>
    </div>
  );
}

function GoogleButton({ onClick }) {
  return (
    <button onClick={onClick} data-testid="account-google-signin-btn"
      className="w-full flex items-center justify-center gap-3 bg-white text-[#0C0C0C] px-8 py-3.5 font-mono text-xs uppercase tracking-[0.15em] hover:bg-[#EDEDED] transition-colors">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
      Continue with Google
    </button>
  );
}

function SignInForm({ onDone }) {
  const [val, setVal] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(val)) { toast.error("Enter a valid email"); return; }
    if (!ok) { toast.error("Please accept the Terms to continue"); return; }
    setLoading(true);
    try {
      const clean = val.trim().toLowerCase();
      await axios.post(`${API}/subscribe`, { email: clean, source: "account_page", consent: true });
      localStorage.setItem("mx_email", clean);
      localStorage.setItem("mx_terms_accepted", new Date().toISOString());
      onDone(clean);
    } catch (e) { toast.error("Something went wrong"); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-sm mx-auto">
      <input value={val} onChange={(e) => setVal(e.target.value)} type="email" placeholder="your@email.com" data-testid="account-signin-input"
        onKeyDown={(e) => e.key === "Enter" && submit()}
        className="w-full bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 text-center font-serif text-lg placeholder:text-[#52525B] transition-colors" />
      <label className="mt-5 flex items-start gap-3 text-left cursor-pointer">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="account-signin-consent" className="mt-1 accent-[#D4AF37]" />
        <span className="text-xs text-[#A1A1AA]">I agree to the <a href="/policy/terms" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Terms</a>, <a href="/policy/privacy" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Privacy Policy</a>, and Research-Use-Only Policy.</span>
      </label>
      <button onClick={submit} disabled={loading || !ok} data-testid="account-signin-btn"
        className="mt-6 w-full bg-[#D4AF37] px-8 py-3.5 text-black text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-40">
        {loading ? "Signing in…" : "Sign in"}
      </button>
    </div>
  );
}
